import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';

// ============================================================
// VALIDATION
// ============================================================
const validateEmail = (value) => {
  if (!value.trim()) return 'Email is required.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address.';
  return '';
};

// ============================================================
// FORGOT PASSWORD COMPONENT
// ============================================================
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Client-side validation
    const emailErr = validateEmail(email);
    if (emailErr) {
      setError(emailErr);
      return;
    }

    setLoading(true);
    try {
      const res = await api.auth.forgotPassword({
        email: email.trim().toLowerCase(),
      });
      // Always show success - the backend does the same to prevent email enumeration
      setSuccess(true);
    } catch (err) {
      // Even on error, show success to prevent email enumeration
      setSuccess(true);
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // STYLES
  // ============================================================
  const colors = {
    bg: '#0a0a0a',
    inputBg: '#1a1a1a',
    border: '#2a2a2a',
    borderFocus: '#00d4aa',
    primary: '#00d4aa',
    error: '#ff4d4d',
    errorBg: 'rgba(255, 77, 77, 0.1)',
    success: '#00d4aa',
    successBg: 'rgba(0, 212, 170, 0.1)',
    textPrimary: '#ffffff',
    textSecondary: '#888888',
  };

  const inputBase = {
    padding: '14px 16px',
    borderRadius: '10px',
    border: `1px solid ${colors.border}`,
    background: colors.inputBg,
    color: colors.textPrimary,
    fontSize: '15px',
    width: '100%',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit',
  };

  return (
    <div style={{
      maxWidth: '440px',
      margin: '0 auto',
      padding: '40px 20px 60px',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '1.75rem',
          fontWeight: '700',
          color: colors.textPrimary,
          marginBottom: '6px',
        }}>
          Forgot Password
        </h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary }}>
          Enter your email and we'll send you a reset link
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div style={{
          background: colors.successBg,
          border: `1px solid ${colors.success}`,
          color: colors.success,
          padding: '16px 20px',
          borderRadius: '10px',
          marginBottom: '20px',
          fontSize: '14px',
        }}>
          <p style={{ fontWeight: '600', marginBottom: '6px' }}>Check your email</p>
          <p style={{ color: colors.textSecondary, margin: 0, lineHeight: '1.5' }}>
            If that email is registered, you will receive a password reset link shortly.
            Check your spam folder if you don't see it.
          </p>
        </div>
      )}

      {/* Error Message */}
      {error && !success && (
        <div style={{
          background: colors.errorBg,
          border: `1px solid ${colors.error}`,
          color: colors.error,
          padding: '12px 16px',
          borderRadius: '10px',
          marginBottom: '20px',
          fontSize: '14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
          <span>⚠️</span> {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '13px',
            fontWeight: '600',
            color: colors.textSecondary,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Email
          </label>
          <input
            type="email"
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(''); }}
            placeholder="your@email.com"
            autoComplete="email"
            autoFocus
            style={inputBase}
            onFocus={(e) => {
              e.target.style.borderColor = colors.borderFocus;
              e.target.style.boxShadow = '0 0 0 3px rgba(0,212,170,0.15)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = colors.border;
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '14px',
            borderRadius: '10px',
            border: 'none',
            background: loading ? '#00b892' : colors.primary,
            color: '#000',
            fontSize: '16px',
            fontWeight: '700',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'background 0.2s, opacity 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          {loading ? (
            <>
              Sending...
            </>
          ) : (
            'Send Reset Link'
          )}
        </button>
      </form>

      {/* Back to Login */}
      <p style={{
        textAlign: 'center',
        marginTop: '24px',
        fontSize: '14px',
        color: colors.textSecondary,
      }}>
        <Link to="/login" style={{ color: colors.primary, fontWeight: '600', textDecoration: 'none' }}>
          Back to Login
        </Link>
      </p>
    </div>
  );
}